/** @module @liquidefy/code-editor/io @version 3.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS — types, engine functions, leaf modules
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  Position,
  Range,
  Selection,
  EditorEvent,
  EditorResult,
  Timestamp,
  DocumentId,
  CursorGroupId,
  MarkerId,
  GrammarId,
  LanguageId,
  CompletionId,
  DiagnosticId,
  CodeActionId,
  ViewportId,
  DecorationId,
  WidgetId,
  EditorInstanceId,
  SnippetId,
  CommandId,
  KeybindingId,
  TerminalId,
  PTYProcessId,
  TerminalSessionId,
  TerminalBufferId,
  MultiplexSessionId,
  MultiplexPaneId,
  RecordingId,
  ShareSessionId,
  ParticipantId,
  CloudTerminalId,
  AIRequestId,
  AIAgentId,
  AIConversationId,
  AIMessageId,
  AIModelId,
  WorkspaceId,
  PanelId,
  TabId,
  TabGroupId,
  GitRepositoryId,
  CommitHash,
  BranchName,
  RemoteName,
  TagName,
  StashId,
  FileId,
  DirectoryId,
  SearchResultId,
  ProjectId,
  DependencyId,
  EnvironmentId,
  TaskId,
  BuildId,
  PluginId,
  PluginManifestId,
  SettingsProfileId,
  DiffId,
  HunkId,
  ReviewThreadId,
  ReviewCommentId,
  EventChannelId,
  StorageNamespace,
  PermissionId,
  DocumentEncoding,
  LineEnding,
  EditOperation,
  DocumentState,
  DocumentSnapshot,
  CursorState,
  CursorGroup,
  ColumnSelection,
  HistoryState,
  HistoryEntry,
  MarkerState,
  MarkerKind,
  MarkerStickiness,
  Token,
  TokenLine,
  TokenizerState,
  GrammarDefinition,
  BracketPair,
  LanguageDefinition,
  CompletionItem,
  CompletionContext,
  HoverInfo,
  DiagnosticInfo,
  CodeAction,
  SignatureHelp,
  LocationLink,
  RenameResult,
  CodeLensData,
  InlayHint,
  FormatOptions,
  SemanticToken,
  ViewportState,
  WordWrapMode,
  MinimapConfig,
  GutterConfig,
  StickyScrollConfig,
  DecorationData,
  ZoneWidget,
  ContentWidget,
  ContentWidgetPositionPreference,
  OverlayWidget,
  OverlayWidgetPositionPreference,
  EditorMode,
  EditorConfig,
  EditorState,
  FocusState,
  DiffEditorState,
  FindState,
  FindMatch,
  SnippetDefinition,
  SnippetSession,
  CommandData,
  CommandRegistry,
  KeybindingData,
  KeybindingResolveResult,
  TerminalType,
  TerminalProfile,
  TerminalState,
  PTYState,
  PTYConfig,
  PTYSignal,
  ShellType,
  TerminalBufferState,
  TerminalColor,
  CellFlags,
  ANSISequence,
  ProcessedOutput,
  TerminalSessionState,
  MultiplexerState,
  MultiplexerType,
  MultiplexSession,
  PaneLayout,
  RecordingState,
  RecordingFrame,
  RecordingBookmark,
  PlaybackState,
  TerminalCollabState,
  ShareConfig,
  ParticipantRole,
  CloudTerminalState,
  CloudProvider,
  TerminalAIState,
  CommandSuggestion,
  AIRequest,
  AIResponse,
  AIContext,
  AIConstraints,
  AIAgentState,
  AIAgentType,
  AIAgentTask,
  AIExecutionPlan,
  AIConversationState,
  AIMessage,
  ConversationType,
  CodeGenerationRequest,
  CodeGenerationContext,
  QualityAssessment,
  SecurityAssessment,
  CodeAnalysisRequest,
  AnalysisTarget,
  ComplexityMetrics,
  HalsteadMetrics,
  DuplicateBlock,
  AnalysisFinding,
  AIModelState,
  AIModelRoutingConfig,
  AICacheState,
  AICacheEntry,
  AICacheConfig,
  PersonalizationState,
  AISecurityConfig,
  AIAuditEntry,
  WorkspaceState,
  WorkspaceLayout,
  WorkspaceCollabState,
  WorkspaceSyncState,
  CollabParticipant,
  SyncConflict,
  PanelState,
  TabState,
  TabGroupState,
  GitRepositoryState,
  GitBranch,
  GitRemote,
  GitTag,
  GitStash,
  GitFileStatus,
  GitCommit,
  GitBlameResult,
  GitStagingHunk,
  GitConflict,
  GitMergeState,
  GitHookName,
  GitHookState,
  GitProviderConfig,
  GitProviderType,
  PullRequestState,
  FileEntry,
  DirectoryEntry,
  FileStat,
  ASTNode,
  SymbolInfo,
  SearchQuery,
  SearchResult,
  SearchMatch,
  OTOperation,
  ProjectState,
  ProjectTask,
  DependencyInfo,
  DependencyGraph,
  MonorepoPackage,
  BuildSystem,
  PluginState,
  PluginManifest,
  PluginContributions,
  SandboxConfig,
  SandboxCapability,
  MarketplaceEntry,
  SettingsState,
  SettingsScope,
  SettingsProfile,
  SettingSchema,
  SettingsMigration,
  DiffAlgorithm,
  DiffResult,
  DiffHunk,
  DiffStats,
  MovedBlock,
  ThreeWayMergeState,
  MergeConflict,
  MergeResolution,
  MergeResolutionStrategy,
  ReviewState,
  ReviewThread,
  ReviewComment,
  CodeSuggestion,
  MonacoAdapterConfig,
  LSPClientConfig,
  DAPClientConfig,
  DAPBreakpoint,
  DAPStackFrame,
  DAPVariable,
  DAPScope,
  ExternalToolConfig,
  EditorEventBus,
  StorageEntry,
  PerformanceSnapshot,
  PerformanceBudget,
  TrustLevel,
  PermissionGrant,
  PermissionRequest,
  LogEntry,
  IODependencies,
  DocumentIOConfig,
  SyntaxIOConfig,
  LanguageIOConfig,
  LayoutIOConfig,
  EditorIOConfig,
  TerminalIOConfig,
  AIIOConfig,
  WorkspaceIOConfig,
  GitIOConfig,
  FileSystemIOConfig,
  ProjectIOConfig,
  PluginsIOConfig,
  SettingsIOConfig,
  DiffIOConfig,
  AdaptersIOConfig,
  GlobalIOConfig,
  FullEditorIOConfig,
  TerminalSuiteIOConfig,
  AIFullIOConfig,
  CollaborationIOConfig,
  DevWorkflowIOConfig,
  CodeIntelligenceIOConfig,
  SearchState,
  SearchSessionId,
  KeybindingsState,
  KeyBinding,
  ThemesState,
  ThemeDefinition,
  ThemeId,
  FoldingRules,
  DocumentSymbol,
  AccessibilityConfig,
  TabStop,
  InputValidationConfig,
  OutputFilteringConfig,
  CodeScanningConfig,
  AIResponseStatus,
  AIContent,
  AIModule,
  AIRequestType,
  AIAgentConfig,
  RoutingStrategy,
  NLContext,
  PluginPermission,
  AIModelInfo,
  AIPerformanceInfo,
} from './code-editor-types.js';

import type { FullReviewState } from './diff/review.js';

import {
  ok,
  err,
  generateId,
  asDocumentId,
  asCursorGroupId,
  asTerminalId,
  asEditorInstanceId,
  asWorkspaceId,
  asGitRepositoryId,
  asProjectId,
  asPluginId,
  asAIRequestId,
  asAIAgentId,
  asAIConversationId,
  asAIModelId,
  asDiffId,
  asReviewThreadId,
  asReviewCommentId,
  asRecordingId,
  asShareSessionId,
  asParticipantId,
  asMultiplexSessionId,
  asEventChannelId,
  asStorageNamespace,
  asPermissionId,
  asSettingsProfileId,
  now,
  emptyDocumentState,
  emptyCursorGroup,
  emptyHistoryState,
  emptyViewportState,
  emptyEditorState,
  emptyTerminalState,
  emptyPTYState,
  emptyTerminalSessionState,
  emptyTerminalBufferState,
  emptyMultiplexerState,
  emptyRecordingState,
  emptyTerminalCollabState,
  emptyCloudTerminalState,
  emptyTerminalAIState,
  emptyAIConversationState,
  emptyAIAgentState,
  emptyAICacheState,
  emptyPersonalizationState,
  emptyWorkspaceState,
  emptyGitRepositoryState,
  emptyProjectState,
  emptyPluginState,
  emptySettingsState,
  emptyDiffResult,
  emptyReviewState,
  emptyPerformanceSnapshot,
  DEFAULT_EDITOR_CONFIG,
  DEFAULT_TERMINAL_PROFILE,
  DEFAULT_PTY_CONFIG,
  DEFAULT_MULTIPLEXER_CONFIG,
  DEFAULT_RECORDING_CONFIG,
  DEFAULT_SHARE_CONFIG,
  DEFAULT_AI_MODEL_CONFIG,
  DEFAULT_AI_CACHE_CONFIG,
  DEFAULT_AI_SECURITY_CONFIG,
  DEFAULT_WORKSPACE_LAYOUT,
  DEFAULT_GIT_CONFIG,
  DEFAULT_FILE_WATCH_CONFIG,
  DEFAULT_SEARCH_QUERY,
  DEFAULT_SANDBOX_RESOURCE_LIMITS,
  DEFAULT_SETTINGS_SCOPE_ORDER,
  DEFAULT_DIFF_CONFIG,
  DEFAULT_PERFORMANCE_BUDGET,
  DEFAULT_FORMAT_OPTIONS,
  MAX_DOCUMENT_SIZE,
  MAX_SCROLLBACK_LINES,
  MAX_SEARCH_RESULTS,
  MAX_UNDO_STACK,
  MAX_COMPLETIONS,
  MAX_DIAGNOSTICS,
  matchGlob,
  humanFileSize,
  fileExtension,
  fileName,
  parentDirectory,
  joinPath,
  normalizePath,
  searchFiles,
  buildSearchRegexFromQuery,
  applyOTOperation,
  transformOT,
} from './code-editor.js';

// Leaf module imports
import * as Doc from './document/document.js';
import * as DocCursor from './document/cursor.js';
import * as DocEdit from './document/edit.js';
import * as DocHistory from './document/history.js';
import * as DocMarkers from './document/markers.js';
import * as SynTok from './syntax/tokenizer.js';
import * as SynGrammar from './syntax/grammar.js';
import * as SynHL from './syntax/highlight.js';
import * as SynLang from './syntax/definition.js';
import * as LangMain from './language/language.js';
import * as LangComp from './language/completion.js';
import * as LangDiag from './language/diagnostics.js';
import * as LangNav from './language/navigation.js';
import * as LayVP from './layout/viewport.js';
import * as LayWrap from './layout/wordwrap.js';
import * as LayGutter from './layout/gutter.js';
import * as LayMini from './layout/minimap.js';
import * as LayDeco from './layout/decorations.js';
import * as LayWidgets from './layout/widgets.js';
import * as LayMain from './layout/layout.js';
import * as EdState from './editor/editor.js';
import * as EdFind from './editor/find.js';
import * as EdSnip from './editor/snippet.js';
import * as EdCmd from './editor/command.js';
import * as EdAccess from './editor/accessibility.js';
import * as EdDiff from './editor/diff.js';
import * as TermMain from './terminal/terminal.js';
import * as TermPTY from './terminal/core/pty.js';
import * as TermBuf from './terminal/core/buffer.js';
import * as TermAnsi from './terminal/core/ansi.js';
import * as TermOut from './terminal/core/output.js';
import * as TermSess from './terminal/core/session.js';
import * as TermMux from './terminal/features/multiplexer.js';
import * as TermRec from './terminal/features/recording.js';
import * as TermCollab from './terminal/features/collaboration.js';
import * as TermCloud from './terminal/integrations/cloud.js';
import * as TermAIInt from './terminal/integrations/ai.js';
import * as AIMain from './ai/ai.js';
import * as AIReq from './ai/core/request.js';
import * as AIResp from './ai/core/response.js';
import * as AIModels from './ai/core/models.js';
import * as AICache from './ai/core/cache.js';
import * as AIAgents from './ai/agents/agents.js';
import * as AIConv from './ai/agents/conversation.js';
import * as AIGen from './ai/agents/generation.js';
import * as AIAnalysis from './ai/agents/analysis.js';
import * as AIPersonal from './ai/agents/personalization.js';
import * as AISec from './ai/safety/security.js';
import * as WSMain from './workspace/workspace.js';
import * as WSLayout from './workspace/layout.js';
import * as WSCollab from './workspace/collab.js';
import * as WSSync from './workspace/sync.js';
import * as GitMain from './git/git.js';
import * as GitRepo from './git/core/repository.js';
import * as GitStaging from './git/core/staging.js';
import * as GitCommitMod from './git/core/commit.js';
import * as GitBranchMod from './git/operations/branch.js';
import * as GitMerge from './git/operations/merge.js';
import * as GitRemoteMod from './git/operations/remote.js';
import * as GitHooks from './git/integration/hooks.js';
import * as GitProviders from './git/integration/providers.js';
import * as FSMain from './filesystem/filesystem.js';
import * as FSTree from './filesystem/tree.js';
import * as FSSearch from './filesystem/search.js';
import * as FSAnalysis from './filesystem/analysis.js';
import * as FSAst from './filesystem/ast.js';
import * as FSCollab from './filesystem/collab.js';
import * as ProjMain from './project/project.js';
import * as ProjDetect from './project/detection.js';
import * as ProjTasks from './project/tasks.js';
import * as ProjDeps from './project/deps.js';
import * as ProjEnv from './project/env.js';
import * as ProjBuild from './project/build.js';
import * as PlugMain from './plugins/plugins.js';
import * as PlugLife from './plugins/lifecycle.js';
import * as PlugSandbox from './plugins/sandbox.js';
import * as PlugMarket from './plugins/marketplace.js';
import * as PlugAPI from './plugins/api.js';
import * as SettMain from './settings/settings.js';
import * as SettMgr from './settings/manager.js';
import * as SettSync from './settings/sync.js';
import * as DiffMain from './diff/diff.js';
import * as DiffComp from './diff/compute.js';
import * as DiffMerge from './diff/merge.js';
import * as DiffRender from './diff/render.js';
import * as DiffReview from './diff/review.js';
import * as AdMon from './adapters/monaco.js';
import * as AdLSP from './adapters/lsp.js';
import * as AdDAP from './adapters/dap.js';
import * as AdTools from './adapters/tools.js';
import * as GlEvents from './global/events.js';
import * as GlInfra from './global/infrastructure.js';
import * as GlStorage from './global/storage.js';
import * as GlPerf from './global/performance.js';
import * as GlPerms from './global/permissions.js';
import * as KbModule from './keybindings/keybindings.js';
import * as KbChords from './keybindings/chords.js';
import * as ThemeModule from './themes/themes.js';
import * as SearchModule from './search/search.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL EVENT BUS
// ═══════════════════════════════════════════════════════════════════════════════

type EventHandler = (event: EditorEvent) => void;

interface InternalEventBus {
  on(pattern: string, handler: EventHandler): void;
  off(pattern: string, handler: EventHandler): void;
  emit(event: EditorEvent): void;
  once(pattern: string, handler: EventHandler): void;
  clear(): void;
}

function createInternalEventBus(): InternalEventBus {
  const handlers = new Map<string, Set<EventHandler>>();
  const onceHandlers = new WeakSet<EventHandler>();

  function matchPattern(pattern: string, domain: string, kind: string): boolean {
    if (pattern === '*') return true;
    const eventType = `${domain}.${kind}`;
    if (pattern === eventType) return true;
    if (pattern.endsWith('.*')) {
      return eventType.startsWith(pattern.slice(0, -1));
    }
    return GlEvents.matchEventPattern(pattern, eventType);
  }

  return {
    on(pattern: string, handler: EventHandler): void {
      let set = handlers.get(pattern);
      if (!set) { set = new Set(); handlers.set(pattern, set); }
      set.add(handler);
    },
    off(pattern: string, handler: EventHandler): void {
      const set = handlers.get(pattern);
      if (set) { set.delete(handler); if (set.size === 0) handlers.delete(pattern); }
    },
    emit(event: EditorEvent): void {
      for (const [pattern, set] of handlers) {
        if (matchPattern(pattern, event.domain, event.kind)) {
          for (const handler of set) {
            handler(event);
            if (onceHandlers.has(handler)) { set.delete(handler); }
          }
        }
      }
    },
    once(pattern: string, handler: EventHandler): void {
      onceHandlers.add(handler);
      this.on(pattern, handler);
    },
    clear(): void {
      handlers.clear();
    },
  };
}

function dispatchEvents(bus: InternalEventBus, events: readonly EditorEvent[]): void {
  for (const event of events) bus.emit(event);
}

// ── Circuit factory imports ─────────────────────────────────
import { createAdaptiveWorkspace } from './circuits/adaptive-workspace.js';
import { createAIAssistedEditor } from './circuits/ai-assisted-editor.js';
import { createAICodeReview } from './circuits/ai-code-review.js';
import { createAIPromptLab } from './circuits/ai-prompt-lab.js';
import { createCodeEditorFull } from './circuits/code-editor-full.js';
import { createCollaborativeEditor } from './circuits/collaborative-editor.js';
import { createDiffReview } from './circuits/diff-review.js';
import { createEditorSession } from './circuits/editor-session.js';
import { createGitWorkspace } from './circuits/git-workspace.js';
import { createIntelligentTerminal } from './circuits/intelligent-terminal.js';
import { createOnboardingExperience } from './circuits/onboarding-experience.js';
import { createProjectNavigator } from './circuits/project-navigator.js';
import { createSearchWorkspace } from './circuits/search-workspace.js';
import { createSmartTerminal } from './circuits/smart-terminal.js';
import { createTerminalSession } from './circuits/terminal-session.js';
import { createThemeApplicator } from './circuits/theme-applicator.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — createCodeEditor(deps) → namespaced API
// ═══════════════════════════════════════════════════════════════════════════════

export function createCodeEditor(deps: Partial<IODependencies> = {}) {
  const bus = createInternalEventBus();

  // Mutable state stores
  const documents = new Map<DocumentId, DocumentState>();
  const cursorGroups = new Map<DocumentId, CursorGroup>();
  const histories = new Map<DocumentId, HistoryState>();
  const viewports = new Map<string, ViewportState>();
  const editors = new Map<EditorInstanceId, EditorState>();
  const terminals = new Map<TerminalId, TerminalState>();
  const ptyStates = new Map<PTYProcessId, PTYState>();
  const terminalSessions = new Map<TerminalSessionId, TerminalSessionState>();
  const terminalBuffers = new Map<TerminalBufferId, TerminalBufferState>();
  const multiplexers = new Map<string, MultiplexerState>();
  const recordings = new Map<RecordingId, RecordingState>();
  const shareStates = new Map<ShareSessionId, TerminalCollabState>();
  const cloudTerminals = new Map<CloudTerminalId, CloudTerminalState>();
  const aiConversations = new Map<AIConversationId, AIConversationState>();
  const aiAgents = new Map<AIAgentId, AIAgentState>();
  const aiModelStates = new Map<AIModelId, AIModelState>();
  let aiCacheState: AICacheState = emptyAICacheState();
  const workspaces = new Map<WorkspaceId, WorkspaceState>();
  const gitRepos = new Map<GitRepositoryId, GitRepositoryState>();
  const projects = new Map<ProjectId, ProjectState>();
  const pluginStates = new Map<PluginId, PluginState>();
  let settingsState: SettingsState = emptySettingsState();
  let fullReviewState: FullReviewState = DiffReview.createReviewState(asDiffId(generateId('diff')));
  let permissionsState = GlPerms.createPermissionsState();
  const permissionGrants: PermissionGrant[] = [];
  let storageState = GlStorage.createStorageState({ backend: 'memory', namespace: asStorageNamespace('default'), maxSizeMb: 100 });
  const storageEntries = new Map<string, StorageEntry>();
  let performanceState = GlPerf.createPerformanceState();
  const performanceHistory: PerformanceSnapshot[] = [];
  let searchState: SearchState = SearchModule.createSearchState();
  let keybindingsState: KeybindingsState = KbModule.createKeybindingsState();
  let themesState: ThemesState = ThemeModule.createThemesState();
  const languageRegistry: LanguageDefinition[] = LangMain.createRegistry();
  const commandRegistryResult = EdCmd.createCommandRegistry();
  const commandRegistry: CommandRegistry = commandRegistryResult.success ? commandRegistryResult.value : { commands: new Map() };

  function emitIfOk<T>(result: EditorResult<T>): EditorResult<T> {
    if (result.success && result.events.length > 0) {
      dispatchEvents(bus, result.events);
    }
    return result;
  }

  // ═══ §4 DOCUMENT NAMESPACE ═══

  const document = {
    create(opts: { content?: string; languageId?: LanguageId; encoding?: DocumentEncoding } = {}): EditorResult<DocumentState> {
      const result = Doc.createDocument(opts.content ?? '', opts.languageId ?? 'plaintext', opts.encoding);
      if (result.success) {
        documents.set(result.value.id, result.value);
        const cgId = asCursorGroupId(generateId('cg'));
        cursorGroups.set(result.value.id, emptyCursorGroup(cgId));
        histories.set(result.value.id, emptyHistoryState());
      }
      return emitIfOk(result);
    },
    close(docId: DocumentId): EditorResult<DocumentSnapshot> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.closeDocument(state);
      if (result.success) {
        documents.delete(docId);
        cursorGroups.delete(docId);
        histories.delete(docId);
      }
      return emitIfOk(result);
    },
    get(docId: DocumentId): DocumentState | undefined {
      return documents.get(docId);
    },
    insert(docId: DocumentId, position: Position, text: string): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.insertAt(state, position, text);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    deleteRange(docId: DocumentId, range: Range): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.deleteRange(state, range);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    replaceRange(docId: DocumentId, range: Range, text: string): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.replaceRange(state, range, text);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    transpose(docId: DocumentId, position: Position): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.transpose(state, position);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    indentLines(docId: DocumentId, startLine: number, endLine: number, tabSize = 2): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      const result = Doc.indentLines(state, lines, tabSize);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    dedentLines(docId: DocumentId, startLine: number, endLine: number, tabSize = 2): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      const result = Doc.dedentLines(state, lines, tabSize);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    toggleComment(docId: DocumentId, startLine: number, endLine: number, commentStr = '//'): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      const result = Doc.toggleComment(state, lines, { lineComment: commentStr });
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    duplicateLine(docId: DocumentId, lineNumber: number): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.duplicateLine(state, lineNumber);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    moveLinesUp(docId: DocumentId, startLine: number, endLine: number): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.moveLinesUp(state, startLine, endLine);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    moveLinesDown(docId: DocumentId, startLine: number, endLine: number): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.moveLinesDown(state, startLine, endLine);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    convertEncoding(docId: DocumentId, target: DocumentEncoding): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.convertEncoding(state, target);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    convertLineEndings(docId: DocumentId, target: LineEnding): EditorResult<DocumentState> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      const result = Doc.convertLineEndings(state, target);
      if (result.success) documents.set(docId, result.value);
      return emitIfOk(result);
    },
    computeStatistics(docId: DocumentId) {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      return Doc.computeStatistics(state);
    },
    getLine(docId: DocumentId, lineNumber: number): EditorResult<string> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      return Doc.getLine(state, lineNumber);
    },
    isDirty(docId: DocumentId): boolean {
      const state = documents.get(docId);
      return state ? Doc.isDocumentDirty(state) : false;
    },
    list(): DocumentId[] {
      return [...documents.keys()];
    },
    createInsertEdit(position: Position, text: string): EditOperation {
      return DocEdit.createInsertEdit(position, text);
    },
    createDeleteEdit(range: Range): EditOperation {
      return DocEdit.createDeleteEdit(range);
    },
    createReplaceEdit(range: Range, text: string): EditOperation {
      return DocEdit.createReplaceEdit(range, text);
    },
    createTransposeEdit(pos: Position): EditOperation {
      return DocEdit.createTransposeEdit({ start: pos, end: pos });
    },
    createIndentEdit(startLine: number, endLine: number, tabSize = 2): EditOperation {
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      return DocEdit.createIndentEdit(lines, tabSize);
    },
    createDedentEdit(startLine: number, endLine: number, tabSize = 2): EditOperation {
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      return DocEdit.createDedentEdit(lines, tabSize, '');
    },
    createCommentToggleEdit(startLine: number, endLine: number, commentStr = '//'): EditOperation {
      const lines = Array.from({ length: endLine - startLine + 1 }, (_, i) => startLine + i);
      return DocEdit.createCommentToggleEdit(lines, { lineComment: commentStr }, '');
    },
    createBracketCompleteEdit(position: Position, openBracket: string, closeBracket = ''): EditOperation {
      return DocEdit.createBracketCompleteEdit(position, openBracket, closeBracket);
    },
    validateEdit(edit: EditOperation, docId: DocumentId): EditorResult<boolean> {
      const state = documents.get(docId);
      if (!state) return err('DOC_NOT_FOUND', `Document ${docId} not found`);
      return DocEdit.validateEdit(edit, state);
    },
    isEditNoOp(edit: EditOperation): boolean {
      return DocEdit.isEditNoOp(edit);
    },
    editsOverlap(a: EditOperation, b: EditOperation): boolean {
      return DocEdit.editsOverlap(a, b);
    },
    transformEdit(edit: EditOperation, against: EditOperation): EditOperation {
      return DocEdit.transformEdit(edit, against);
    },
    composeEdits(edits: EditOperation[]): EditOperation[] {
      return DocEdit.composeEdits(edits);
    },
    invertEdit(edit: EditOperation, originalContent: string): EditOperation {
      return DocEdit.invertEdit(edit, originalContent);
    },
    normalizeEdits(edits: EditOperation[]): EditOperation[] {
      return DocEdit.normalizeEdits(edits);
    },
    applyEditToContent(content: string, edit: EditOperation): string {
      return DocEdit.applyEditToContent(content, edit);
    },
    applyEditsToContent(content: string, edits: EditOperation[]): string {
      return DocEdit.applyEditsToContent(content, edits);
    },
    computeEditDelta(edit: EditOperation): number {
      return DocEdit.computeEditDelta(edit);
    },
    computeNewPosition(position: Position, edit: EditOperation): Position {
      return DocEdit.computeNewPosition(position, edit);
    },
  };

  // ═══ §5 CURSOR NAMESPACE ═══

  const cursor = {
    get(docId: DocumentId): CursorGroup | undefined {
      return cursorGroups.get(docId);
    },
    moveUp(docId: DocumentId, lines?: number): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const updated = DocCursor.moveUp(primary, state.content, lines);
      const newGroup: CursorGroup = { ...group, cursors: group.cursors.map((c, i) => i === group.primaryIndex ? updated : c) };
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_MOVED', 'Cursor moved up', [{ domain: 'cursor', kind: 'changed', timestamp: now() }]);
    },
    moveDown(docId: DocumentId, lines?: number): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const updated = DocCursor.moveDown(primary, state.content, lines);
      const newGroup: CursorGroup = { ...group, cursors: group.cursors.map((c, i) => i === group.primaryIndex ? updated : c) };
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_MOVED', 'Cursor moved down', [{ domain: 'cursor', kind: 'changed', timestamp: now() }]);
    },
    moveLeft(docId: DocumentId): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const updated = DocCursor.moveLeft(primary, state.content);
      const newGroup: CursorGroup = { ...group, cursors: group.cursors.map((c, i) => i === group.primaryIndex ? updated : c) };
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_MOVED', 'Cursor moved left', [{ domain: 'cursor', kind: 'changed', timestamp: now() }]);
    },
    moveRight(docId: DocumentId): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const updated = DocCursor.moveRight(primary, state.content);
      const newGroup: CursorGroup = { ...group, cursors: group.cursors.map((c, i) => i === group.primaryIndex ? updated : c) };
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_MOVED', 'Cursor moved right', [{ domain: 'cursor', kind: 'changed', timestamp: now() }]);
    },
    selectWord(docId: DocumentId): EditorResult<Selection> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const sel = DocCursor.selectWord(primary, state.content);
      return ok(sel, 'SELECTION_CREATED', 'Word selected');
    },
    selectLine(docId: DocumentId): EditorResult<Selection> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Document or cursor not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const sel = DocCursor.selectLine(primary, state.content);
      return ok(sel, 'SELECTION_CREATED', 'Line selected');
    },
    selectAll(docId: DocumentId): EditorResult<Selection> {
      const state = documents.get(docId);
      if (!state) return err('NOT_FOUND', 'Document not found');
      const sel = DocCursor.selectAll(state.content);
      return ok(sel, 'SELECTION_CREATED', 'All selected');
    },
    addCursor(docId: DocumentId, position: Position): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      if (!group) return err('NOT_FOUND', 'Cursor group not found');
      const newGroup = DocCursor.addCursor(group, position);
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_ADDED', 'Cursor added', [{ domain: 'cursor', kind: 'created', timestamp: now() }]);
    },
    removeCursor(docId: DocumentId, index: number): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      if (!group) return err('NOT_FOUND', 'Cursor group not found');
      const newGroup = DocCursor.removeCursor(group, index);
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_REMOVED', 'Cursor removed', [{ domain: 'cursor', kind: 'deleted', timestamp: now() }]);
    },
    addCursorAbove(docId: DocumentId): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Not found');
      const newGroup = DocCursor.addCursorAbove(group, state.content);
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_ADDED', 'Cursor added above', [{ domain: 'cursor', kind: 'created', timestamp: now() }]);
    },
    addCursorBelow(docId: DocumentId): EditorResult<CursorGroup> {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Not found');
      const newGroup = DocCursor.addCursorBelow(group, state.content);
      cursorGroups.set(docId, newGroup);
      return ok(newGroup, 'CURSOR_ADDED', 'Cursor added below', [{ domain: 'cursor', kind: 'created', timestamp: now() }]);
    },
    createColumnSelection(start: Position, end: Position): ColumnSelection {
      return DocCursor.createColumnSelection(start, end);
    },
    cursorContext(docId: DocumentId) {
      const group = cursorGroups.get(docId);
      const state = documents.get(docId);
      if (!group || !state) return err('NOT_FOUND', 'Not found');
      const primary = group.cursors[group.primaryIndex];
      if (!primary) return err('NOT_FOUND', 'Primary cursor not found');
      const ctx = DocCursor.cursorContext(primary, state.content);
      return ok(ctx, 'OK', 'Context retrieved');
    },
  };

  // ═══ §6 HISTORY NAMESPACE ═══

  const history = {
    push(docId: DocumentId, entry: HistoryEntry): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.pushToHistory(state, entry.operations, { before: entry.beforeCursors, after: entry.afterCursors });
      if (result.success) histories.set(docId, result.value);
      return emitIfOk(result);
    },
    undo(docId: DocumentId): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.undoOperation(state);
      if (result.success) histories.set(docId, result.value.state);
      return result.success ? { ...result, value: result.value.state } : result;
    },
    redo(docId: DocumentId): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.redoOperation(state);
      if (result.success) histories.set(docId, result.value.state);
      return result.success ? { ...result, value: result.value.state } : result;
    },
    canUndo(docId: DocumentId): boolean {
      const state = histories.get(docId);
      return state ? DocHistory.canUndo(state) : false;
    },
    canRedo(docId: DocumentId): boolean {
      const state = histories.get(docId);
      return state ? DocHistory.canRedo(state) : false;
    },
    createBranch(docId: DocumentId, label: string): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.createHistoryBranch(state, label);
      if (result.success) histories.set(docId, result.value);
      return emitIfOk(result);
    },
    switchBranch(docId: DocumentId, branchId: string): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.switchHistoryBranch(state, branchId);
      if (result.success) histories.set(docId, result.value);
      return emitIfOk(result);
    },
    serialize(docId: DocumentId) {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      return ok(DocHistory.serializeHistory(state), 'OK', 'History serialized');
    },
    clear(docId: DocumentId): EditorResult<HistoryState> {
      const state = histories.get(docId);
      if (!state) return err('NOT_FOUND', 'History not found');
      const result = DocHistory.clearHistory(state);
      if (result.success) histories.set(docId, result.value);
      return emitIfOk(result);
    },
  };

  // ═══ §7 MARKERS NAMESPACE ═══

  const documentMarkersStates = new Map<string, ReturnType<typeof DocMarkers.createEmptyMarkersState>>();

  const markers = {
    add(_docId: DocumentId, _marker: MarkerState): EditorResult<MarkerState[]> {
      return err('NOT_IMPLEMENTED', 'Use markers.addBookmark or markers.addDiagnosticMarker');
    },
    remove(_docId: DocumentId, _markerId: MarkerId): EditorResult<MarkerState[]> {
      return err('NOT_IMPLEMENTED', 'Use markers.removeBookmark');
    },
    getAll(_docId: DocumentId): MarkerState[] {
      return [];
    },
    getByKind(_docId: DocumentId, _kind: MarkerKind): MarkerState[] {
      return [];
    },
    clearAll(_docId: DocumentId): void {
      // no-op
    },
  };

  // ═══ §8 SYNTAX NAMESPACE ═══

  let grammarRegistry: ReturnType<typeof SynGrammar.createGrammarRegistry> = SynGrammar.createGrammarRegistry();
  let synLangRegistry: ReturnType<typeof SynLang.createLanguageRegistry> = SynLang.createLanguageRegistry();

  const syntax = {
    tokenizeLine(line: string, state: TokenizerState, grammar: GrammarDefinition): { tokens: Token[]; endState: TokenizerState } {
      return SynTok.tokenizeLine(line, state, grammar);
    },
    tokenizeLines(lines: string[], grammar: GrammarDefinition): TokenLine[] {
      return SynTok.tokenizeLines(lines, grammar);
    },
    retokenizeRange(tokenLines: readonly TokenLine[], changedRange: Range, content: string, grammar: GrammarDefinition): TokenLine[] {
      return SynTok.retokenizeRange(tokenLines, changedRange, content, grammar);
    },
    resolveScopes(scopes: readonly string[], grammar: GrammarDefinition) {
      return SynTok.resolveScopes(scopes, grammar);
    },
    tokenToStyle(token: Token, theme: readonly SynHL.ThemeRule[]): SynHL.TokenStyle {
      return SynHL.tokenToStyle(token, theme);
    },
    findMatchingBracket(content: string, position: Position, brackets: readonly BracketPair[]): Position | null {
      return SynHL.findMatchingBracket(content, position, brackets);
    },
    registerGrammar(languageId: LanguageId, grammar: GrammarDefinition): void {
      grammarRegistry = SynGrammar.registerGrammar(grammarRegistry, languageId, grammar);
    },
    getGrammarForLanguage(languageId: LanguageId): EditorResult<GrammarDefinition> {
      return SynGrammar.getGrammarForLanguage(grammarRegistry, languageId);
    },
    registerLanguageDef(def: LanguageDefinition): EditorResult<SynLang.LanguageRegistryState> {
      const result = SynLang.registerLanguage(synLangRegistry, def);
      if (result.success) synLangRegistry = result.value;
      return result;
    },
    detectLanguageFromPath(path: string): LanguageId {
      return SynLang.detectLanguage(synLangRegistry, path);
    },
  };

  // ═══ §9 LANGUAGE NAMESPACE ═══

  const language = {
    registerLanguage(definition: LanguageDefinition): EditorResult<LanguageDefinition[]> {
      const updated = LangMain.registerLanguage(languageRegistry, definition);
      return ok(updated, 'LANGUAGE_REGISTERED', `Language ${definition.id} registered`, [{ domain: 'language', kind: 'created', timestamp: now() }]);
    },
    detectFromPath(path: string): LanguageDefinition | null {
      return LangMain.detectLanguageFromPath(path, languageRegistry);
    },
    detectFromContent(content: string): LanguageDefinition | null {
      return LangMain.detectLanguageFromContent(content, languageRegistry);
    },
    filterCompletions(items: readonly CompletionItem[], prefix: string, maxResults?: number): CompletionItem[] {
      return LangComp.filterCompletions(items, prefix, maxResults !== undefined ? { maxResults } : undefined);
    },
    scoreCompletion(item: CompletionItem, context: LangComp.CompletionScoringContext): number {
      return LangComp.scoreCompletion(item, context);
    },
    addDiagnostic(diagnostics: readonly LangDiag.IdentifiedDiagnostic[], diagnostic: LangDiag.IdentifiedDiagnostic): LangDiag.IdentifiedDiagnostic[] {
      return LangDiag.addDiagnostic(diagnostics, diagnostic);
    },
    applyCodeAction(content: string, action: CodeAction): EditorResult<string> {
      return LangDiag.applyCodeAction(content, action);
    },
    findDefinition(content: string, position: Position, name: string): LocationLink[] {
      const symbols = LangNav.getDocumentSymbols(content, 'plaintext' as LanguageId);
      return LangNav.findDefinition(symbols, name, position, content);
    },
    findReferences(content: string, name: string, includeDeclaration?: boolean): LocationLink[] {
      return LangNav.findReferences(content, name, includeDeclaration);
    },
    prepareRename(content: string, position: Position): { range: Range; placeholder: string } | null {
      const symbols = LangNav.getDocumentSymbols(content, 'plaintext' as LanguageId);
      return LangNav.prepareRename(content, position, symbols);
    },
    computeRenameEdits(content: string, position: Position, newName: string): EditorResult<RenameResult> {
      const symbols = LangNav.getDocumentSymbols(content, 'plaintext' as LanguageId);
      return LangNav.computeRenameEdits(content, position, newName, symbols);
    },
    getDocumentSymbols(content: string, languageId: LanguageId): DocumentSymbol[] {
      return LangNav.getDocumentSymbols(content, languageId);
    },
  };

  // ═══ §10 LAYOUT NAMESPACE ═══

  const layout = {
    computeViewport(scrollTop: number, containerHeight: number, lineHeight: number, totalLines: number, charWidth: number): EditorResult<ViewportState> {
      return LayVP.computeViewport(scrollTop, containerHeight, lineHeight, totalLines, charWidth);
    },
    scrollToLine(viewport: ViewportState, line: number, totalLines: number): EditorResult<ViewportState> {
      return LayVP.scrollToLine(viewport, line, totalLines);
    },
    wrapLine(content: string, maxColumn: number, mode: WordWrapMode, tabSize = 2) {
      return LayWrap.wrapLine(content, maxColumn, mode, tabSize);
    },
    computeFoldingRanges(content: string, languageId: LanguageId, rules: FoldingRules | null = null) {
      return LayGutter.computeFoldingRanges(content, languageId, rules);
    },
    computeMinimapViewport(editorViewport: ViewportState, totalLines: number, config: MinimapConfig) {
      return LayMini.computeMinimapViewport(editorViewport, totalLines, config);
    },
    computeStickyHeaders(content: string, symbols: DocumentSymbol[], viewport: ViewportState, config: StickyScrollConfig) {
      return LayGutter.computeStickyHeaders(content, symbols, viewport, config);
    },
    createDecorationState(): LayDeco.DecorationState {
      return LayDeco.createDecorationState();
    },
    createZoneWidget(id: WidgetId, afterLine: number, heightInLines: number): EditorResult<ZoneWidget> {
      return LayWidgets.createZoneWidget(id, afterLine, heightInLines);
    },
    createContentWidget(id: WidgetId, position: Position, preference?: ContentWidgetPositionPreference[]): EditorResult<ContentWidget> {
      return LayWidgets.createContentWidget(id, position, preference);
    },
    createOverlayWidget(id: WidgetId, preference: OverlayWidgetPositionPreference): EditorResult<OverlayWidget> {
      return LayWidgets.createOverlayWidget(id, preference);
    },
  };

  // ═══ §11 EDITOR NAMESPACE ═══

  const editor = {
    create(docId: DocumentId, config?: Partial<EditorConfig>): EditorResult<EditorState> {
      const id = asEditorInstanceId(generateId('ed'));
      const result = EdState.createEditor(id, docId, config);
      if (result.success) editors.set(result.value.id, result.value);
      return emitIfOk(result);
    },
    get(editorId: EditorInstanceId): EditorState | undefined {
      return editors.get(editorId);
    },
    setMode(editorId: EditorInstanceId, mode: EditorMode): EditorResult<EditorState> {
      const state = editors.get(editorId);
      if (!state) return err('NOT_FOUND', 'Editor not found');
      const result = EdState.setMode(state, mode);
      if (result.success) editors.set(editorId, result.value);
      return emitIfOk(result);
    },
    setFocus(editorId: EditorInstanceId, focusTarget: FocusState['focusTarget']): EditorResult<EditorState> {
      const state = editors.get(editorId);
      if (!state) return err('NOT_FOUND', 'Editor not found');
      const result = EdState.setFocus(state, focusTarget);
      if (result.success) editors.set(editorId, result.value);
      return emitIfOk(result);
    },
    createDiffEditor(originalId: DocumentId, modifiedId: DocumentId): EditorResult<DiffEditorState> {
      return EdState.createDiffEditor(originalId, modifiedId);
    },
    list(): EditorInstanceId[] {
      return [...editors.keys()];
    },
    getDiffSummary(state: DiffEditorState) {
      return EdDiff.getDiffSummary(state);
    },
    navigateDiff(state: DiffEditorState, direction: 'next' | 'prev') {
      return EdDiff.navigateToDiffChange(state, direction);
    },
    getAccessibilityState(config: AccessibilityConfig) {
      return EdAccess.createAccessibilityState(config);
    },
  };

  // ═══ §12 FIND NAMESPACE ═══

  const find = {
    createState(opts?: Partial<FindState>): EditorResult<FindState> {
      return EdFind.createFindState(opts);
    },
    findAll(content: string, state: FindState): EditorResult<FindMatch[]> {
      return EdFind.findAll(content, state);
    },
    findNext(content: string, state: FindState, fromPosition: Position): EditorResult<FindMatch | null> {
      return EdFind.findNext(content, state, fromPosition);
    },
    replaceOne(content: string, match: FindMatch, replacement: string, preserveCase = false): EditorResult<string> {
      return EdFind.replaceOne(content, match, replacement, preserveCase);
    },
    replaceAll(content: string, matches: FindMatch[], replacement: string): EditorResult<string> {
      return EdFind.replaceAll(content, matches, replacement);
    },
  };

  // ═══ §13 SNIPPETS NAMESPACE ═══

  const snippets = {
    parse(body: string[]) {
      return EdSnip.parseSnippetBody(body);
    },
    insert(content: string, position: Position, snippet: SnippetDefinition, variables: Record<string, string> = {}): EditorResult<{ content: string; session: SnippetSession; edits: EditOperation[] }> {
      return EdSnip.insertSnippet(content, position, snippet, variables);
    },
    nextTabStop(session: SnippetSession): SnippetSession {
      return EdSnip.nextTabStop(session);
    },
    previousTabStop(session: SnippetSession): SnippetSession {
      return EdSnip.previousTabStop(session);
    },
    createSession(snippetId: SnippetId, tabStops: TabStop[], placeholders: Map<number, string>): SnippetSession {
      return EdSnip.createSnippetSession(snippetId, tabStops, placeholders);
    },
  };

  // ═══ §14 COMMANDS NAMESPACE ═══

  const commands = {
    register(command: CommandData): EditorResult<CommandRegistry> {
      return EdCmd.registerCommand(commandRegistry, command);
    },
    unregister(commandId: CommandId): EditorResult<CommandRegistry> {
      return EdCmd.unregisterCommand(commandRegistry, commandId);
    },
    resolveKeybinding(keybindings: KeybindingData[], key: string, context: Record<string, unknown>): KeybindingResolveResult | null {
      return EdCmd.resolveKeybinding(keybindings, key, context);
    },
    evaluateWhenClause(clause: string, context: Record<string, unknown>): boolean {
      return EdCmd.evaluateWhenClause(clause, context);
    },
    list() {
      return EdCmd.listCommands(commandRegistry);
    },
    search(query: string) {
      return EdCmd.searchCommands(commandRegistry, query);
    },
  };

  // ═══ §15 KEYBINDINGS NAMESPACE (3.0.0) ═══

  const keybindings = {
    getState(): KeybindingsState {
      return keybindingsState;
    },
    register(binding: KeyBinding): EditorResult<KeybindingsState> {
      const result = KbModule.registerBinding(keybindingsState, binding);
      if (result.success) keybindingsState = result.value;
      return result;
    },
    unregister(id: KeybindingId): EditorResult<KeybindingsState> {
      const result = KbModule.unregisterBinding(keybindingsState, id);
      if (result.success) keybindingsState = result.value;
      return result;
    },
    resolveKey(key: string, context: Record<string, unknown>) {
      return KbModule.resolveKey(keybindingsState, key, context);
    },
    beginChord(key: string) {
      const updated = KbChords.startChord(keybindingsState, key);
      keybindingsState = updated;
      return updated;
    },
    cancelChord() {
      const updated = KbChords.cancelChord(keybindingsState);
      keybindingsState = updated;
      return updated;
    },
    listAll() {
      return keybindingsState.bindings;
    },
  };

  // ═══ §16 THEMES NAMESPACE (3.0.0) ═══

  const themes = {
    getState(): ThemesState {
      return themesState;
    },
    register(theme: ThemeDefinition): EditorResult<ThemesState> {
      const result = ThemeModule.registerTheme(themesState, theme);
      if (result.success) themesState = result.value;
      return result;
    },
    activate(themeId: ThemeId): EditorResult<ThemesState> {
      const result = ThemeModule.activateTheme(themesState, themeId);
      if (result.success) themesState = result.value;
      return result;
    },
    getActive(): ThemeDefinition | undefined {
      return ThemeModule.getActiveTheme(themesState);
    },
    listAll(): ReadonlyArray<ThemeDefinition> {
      return ThemeModule.listThemes(themesState);
    },
  };

  // ═══ §17 TERMINAL NAMESPACE ═══

  const terminal = {
    create(profile?: Partial<TerminalProfile>): EditorResult<TerminalState> {
      const id = asTerminalId(generateId('term'));
      const base = emptyTerminalState(id);
      terminals.set(id, base);
      bus.emit({ domain: 'terminal', kind: 'created', timestamp: now(), payload: { id } });
      return ok(base, 'TERMINAL_CREATED', 'Terminal created', [{ domain: 'terminal', kind: 'created', timestamp: now() }]);
    },
    get(termId: TerminalId): TerminalState | undefined {
      return terminals.get(termId);
    },
    close(termId: TerminalId): EditorResult<boolean> {
      if (!terminals.has(termId)) return err('NOT_FOUND', 'Terminal not found');
      terminals.delete(termId);
      bus.emit({ domain: 'terminal', kind: 'closed', timestamp: now(), payload: { id: termId } });
      return ok(true, 'TERMINAL_CLOSED', 'Terminal closed');
    },
    list(): TerminalId[] {
      return [...terminals.keys()];
    },
  };

  // ═══ §18 PTY NAMESPACE ═══

  const pty = {
    create(config: PTYConfig): EditorResult<PTYState> {
      const id = generateId('pty');
      const result = TermPTY.createPTY(id, config);
      if (result.success) ptyStates.set(result.value.processId, result.value);
      return emitIfOk(result);
    },
    resize(ptyId: PTYProcessId, cols: number, rows: number): EditorResult<PTYState> {
      const state = ptyStates.get(ptyId);
      if (!state) return err('NOT_FOUND', 'PTY not found');
      const result = TermPTY.resizePTY(state, cols, rows);
      if (result.success) ptyStates.set(ptyId, result.value);
      return emitIfOk(result);
    },
    start(ptyId: PTYProcessId, config?: PTYConfig): EditorResult<PTYState> {
      const state = ptyStates.get(ptyId);
      if (!state) return err('NOT_FOUND', 'PTY not found');
      const result = TermPTY.startPTY(state);
      if (result.success) {
        ptyStates.set(ptyId, result.value);
        if (deps.ptyBackend && config) deps.ptyBackend.spawn(config);
      }
      return emitIfOk(result);
    },
    kill(ptyId: PTYProcessId, signal: PTYSignal = 'SIGTERM' as PTYSignal): EditorResult<PTYState> {
      const state = ptyStates.get(ptyId);
      if (!state) return err('NOT_FOUND', 'PTY not found');
      const result = TermPTY.killPTY(state, signal);
      if (result.success) {
        ptyStates.set(ptyId, result.value);
        if (deps.ptyBackend) deps.ptyBackend.kill(ptyId, signal);
      }
      return emitIfOk(result);
    },
    detectShell(path: string): ShellType {
      return TermPTY.detectShell(path);
    },
    shellExecutable(shell: ShellType): string {
      return TermPTY.shellExecutable(shell);
    },
  };

  // ═══ §19 SESSION NAMESPACE ═══

  const session = {
    create(shell: ShellType, cwd?: string): EditorResult<TerminalSessionState> {
      const id = generateId('sess');
      const result = TermSess.createSession(id, shell, undefined, cwd);
      if (result.success) terminalSessions.set(result.value.id, result.value);
      return emitIfOk(result);
    },
    addCommand(sessionId: TerminalSessionId, command: string, cwd = ''): EditorResult<TerminalSessionState> {
      const state = terminalSessions.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Session not found');
      const updated = TermSess.addCommand(state, command, cwd);
      terminalSessions.set(sessionId, updated);
      return ok(updated, 'COMMAND_ADDED', 'Command added to history');
    },
    searchHistory(sessionId: TerminalSessionId, query: string) {
      const state = terminalSessions.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Session not found');
      return ok(TermSess.searchHistory(state.history, query), 'OK', 'History searched');
    },
    updateEnv(sessionId: TerminalSessionId, key: string, value: string): EditorResult<TerminalSessionState> {
      const state = terminalSessions.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Session not found');
      const updated = TermSess.updateEnv(state, key, value);
      terminalSessions.set(sessionId, updated);
      return ok(updated, 'ENV_UPDATED', 'Environment updated');
    },
    updateCwd(sessionId: TerminalSessionId, cwd: string): EditorResult<TerminalSessionState> {
      const state = terminalSessions.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Session not found');
      const updated = TermSess.updateCwd(state, cwd);
      terminalSessions.set(sessionId, updated);
      return ok(updated, 'CWD_UPDATED', 'Working directory updated');
    },
  };

  // ═══ §20 MULTIPLEXER NAMESPACE ═══

  const multiplexer = {
    create(type: MultiplexerType = 'built-in'): EditorResult<{ id: string; state: MultiplexerState }> {
      const muxId = generateId('mux');
      const state = TermMux.createMultiplexer(type);
      multiplexers.set(muxId, state);
      return ok({ id: muxId, state }, 'MUX_CREATED', 'Multiplexer created', [{ domain: 'multiplexer', kind: 'created', timestamp: now() }]);
    },
    createSession(muxId: string, name: string = 'default'): EditorResult<MultiplexerState> {
      const state = multiplexers.get(muxId);
      if (!state) return err('NOT_FOUND', 'Multiplexer not found');
      const result = TermMux.createSession(state, name);
      if (result.success) multiplexers.set(muxId, result.value);
      return emitIfOk(result);
    },
    splitHorizontal(muxId: string, paneId: MultiplexPaneId, terminalId: TerminalId): EditorResult<MultiplexerState> {
      const state = multiplexers.get(muxId);
      if (!state) return err('NOT_FOUND', 'Multiplexer not found');
      const result = TermMux.splitPaneHorizontal(state, paneId, terminalId);
      if (result.success) multiplexers.set(muxId, result.value);
      return emitIfOk(result);
    },
    splitVertical(muxId: string, paneId: MultiplexPaneId, terminalId: TerminalId): EditorResult<MultiplexerState> {
      const state = multiplexers.get(muxId);
      if (!state) return err('NOT_FOUND', 'Multiplexer not found');
      const result = TermMux.splitPaneVertical(state, paneId, terminalId);
      if (result.success) multiplexers.set(muxId, result.value);
      return emitIfOk(result);
    },
  };

  // ═══ §21 BUFFER NAMESPACE ═══

  const buffer = {
    create(cols: number, rows: number, scrollbackLimit?: number): EditorResult<TerminalBufferState> {
      const id = generateId('buf');
      const result = TermBuf.createBuffer(id, cols, rows, scrollbackLimit !== undefined ? { maxLines: scrollbackLimit } : undefined);
      if (result.success) terminalBuffers.set(result.value.id, result.value);
      return result;
    },
    get(bufId: TerminalBufferId): TerminalBufferState | undefined {
      return terminalBuffers.get(bufId);
    },
    writeCharacter(bufId: TerminalBufferId, char: string, fg: TerminalColor = { mode: 'default' }, bg: TerminalColor = { mode: 'default' }, flags: CellFlags = 0): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.writeCharacter(state, char, fg, bg, flags);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'CHAR_WRITTEN', 'Character written');
    },
    writeString(bufId: TerminalBufferId, text: string, fg: TerminalColor = { mode: 'default' }, bg: TerminalColor = { mode: 'default' }, flags: CellFlags = 0): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.writeString(state, text, fg, bg, flags);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'STRING_WRITTEN', 'String written');
    },
    newline(bufId: TerminalBufferId): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.newline(state);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'NEWLINE', 'Newline written');
    },
    carriageReturn(bufId: TerminalBufferId): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.carriageReturn(state);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'CR', 'Carriage return');
    },
    backspace(bufId: TerminalBufferId): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.backspace(state);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'BS', 'Backspace');
    },
    tab(bufId: TerminalBufferId, tabStops?: number): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.tab(state, tabStops);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'TAB', 'Tab');
    },
    clearLine(bufId: TerminalBufferId, lineIndex: number, start?: number, end?: number): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.clearLine(state, lineIndex, start, end);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'LINE_CLEARED', 'Line cleared');
    },
    clearScreen(bufId: TerminalBufferId, mode: 'below' | 'above' | 'all' | 'scrollback' = 'all'): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.clearScreen(state, mode);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'SCREEN_CLEARED', 'Screen cleared');
    },
    scrollUp(bufId: TerminalBufferId, lines: number): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.scrollUp(state, lines);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'SCROLLED', 'Scrolled up');
    },
    scrollDown(bufId: TerminalBufferId, lines: number): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.scrollDown(state, lines);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'SCROLLED', 'Scrolled down');
    },
    resize(bufId: TerminalBufferId, cols: number, rows: number): EditorResult<TerminalBufferState> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      const updated = TermBuf.resizeBuffer(state, cols, rows);
      terminalBuffers.set(bufId, updated);
      return ok(updated, 'RESIZED', 'Buffer resized');
    },
    moveCursor(_bufId: TerminalBufferId, _row: number, _col: number): EditorResult<TerminalBufferState> {
      return err('NOT_IMPLEMENTED', 'moveCursor: use ANSI CSI sequences to position cursor');
    },
    getVisibleContent(bufId: TerminalBufferId): EditorResult<string[]> {
      const state = terminalBuffers.get(bufId);
      if (!state) return err('NOT_FOUND', 'Buffer not found');
      return ok(TermBuf.getVisibleContent(state), 'OK', 'Lines retrieved');
    },
  };

  // ═══ §22 ANSI NAMESPACE ═══

  const ansi = {
    parse(data: string): ANSISequence[] {
      return TermAnsi.parseSequences(data);
    },
    parseCSI(raw: string): ANSISequence {
      return TermAnsi.parseCSI(raw);
    },
    parseSGR(params: number[]) {
      return TermAnsi.parseSGR(params);
    },
    stripCodes(text: string): string {
      // Remove ANSI escape sequences via simple regex
      return text.replace(/\x1b\[[0-9;]*[mGKHF]/g, '').replace(/\x1b[()][AB012]/g, '');
    },
  };

  // ═══ §23 OUTPUT NAMESPACE ═══

  const output = {
    classify(text: string) {
      return TermOut.classifyOutput(text);
    },
    process(text: string) {
      return TermOut.processOutput(text);
    },
    extractEntities(text: string) {
      return TermOut.extractEntities(text);
    },
    detectFormat(text: string) {
      return TermOut.detectFormat(text);
    },
  };

  // ═══ §24 CLOUD NAMESPACE ═══

  const cloud = {
    create(provider: CloudProvider): EditorResult<CloudTerminalState> {
      const result = TermCloud.createCloudConfig(generateId('cloud'), provider);
      if (result.success) cloudTerminals.set(result.value.id, result.value);
      return emitIfOk(result);
    },
    get(id: CloudTerminalId): CloudTerminalState | undefined {
      return cloudTerminals.get(id);
    },
    connect(id: CloudTerminalId): EditorResult<CloudTerminalState> {
      const state = cloudTerminals.get(id);
      if (!state) return err('NOT_FOUND', 'Cloud terminal not found');
      const updated = TermCloud.updateConnection(state, 'connected');
      cloudTerminals.set(id, updated);
      return ok(updated, 'CLOUD_CONNECTED', 'Cloud terminal connected');
    },
    disconnect(id: CloudTerminalId): EditorResult<CloudTerminalState> {
      const state = cloudTerminals.get(id);
      if (!state) return err('NOT_FOUND', 'Cloud terminal not found');
      const updated = TermCloud.updateConnection(state, 'disconnected');
      cloudTerminals.set(id, updated);
      return ok(updated, 'CLOUD_DISCONNECTED', 'Cloud terminal disconnected');
    },
  };

  // ═══ §25 TERMINAL AI NAMESPACE ═══

  const terminalAI = {
    getSuggestions(command: string, context: NLContext, count?: number): CommandSuggestion[] {
      return TermAIInt.generateSuggestions({ entries: [], maxSize: 1000 }, context, count);
    },
    analyzeError(output: string, context: NLContext) {
      return TermAIInt.analyzeError(output, context);
    },
    explainError(error: string): string {
      return TermAIInt.explainError(error);
    },
  };

  // ═══ §26 AI NAMESPACE ═══

  const ai = {
    createRequest(module: AIModule, type: AIRequestType, context: AIContext, overrides?: Partial<Pick<AIRequest, 'constraints' | 'priority' | 'qualityThreshold'>>): AIRequest {
      return AIReq.createRequest(module, type, context, overrides);
    },
    createResponse(requestId: AIRequestId, status: AIResponseStatus, content: AIContent, model: AIModelInfo, performance: AIPerformanceInfo, confidence: number): AIResponse {
      return AIResp.createResponse(requestId, status, content, model, performance, confidence);
    },
    createConversation(id: AIConversationId, type: ConversationType): AIConversationState {
      const state = AIConv.createConversation(id, type);
      aiConversations.set(id, state);
      return state;
    },
    addMessage(conversationId: AIConversationId, text: string): EditorResult<AIConversationState> {
      const state = aiConversations.get(conversationId);
      if (!state) return err('NOT_FOUND', 'Conversation not found');
      const updated = AIConv.addUserMessage(state, text);
      aiConversations.set(conversationId, updated);
      return ok(updated, 'MESSAGE_ADDED', 'Message added');
    },
    createAgent(id: AIAgentId, type: AIAgentType, config?: Partial<AIAgentConfig>): AIAgentState {
      const state = AIAgents.createAgent(id as string, type, config);
      aiAgents.set(id, state);
      return state;
    },
    assignTask(agentId: AIAgentId, task: AIAgentTask): EditorResult<AIAgentState> {
      const state = aiAgents.get(agentId);
      if (!state) return err('NOT_FOUND', 'Agent not found');
      const result = AIAgents.assignTask(state, task);
      if (result.success) aiAgents.set(agentId, result.value);
      return result;
    },
    buildGenerationRequest(...args: Parameters<typeof AIGen.buildGenerationRequest>) {
      return AIGen.buildGenerationRequest(...args);
    },
    buildAnalysisRequest(...args: Parameters<typeof AIAnalysis.buildAnalysisRequest>) {
      return AIAnalysis.buildAnalysisRequest(...args);
    },
    lookupCache(key: string): AICacheEntry | null {
      return AICache.lookupCache(aiCacheState, key);
    },
    insertCache(request: AIRequest, response: AIResponse, config?: Partial<AICacheConfig>): void {
      aiCacheState = AICache.insertCache(aiCacheState, request, response, config);
    },
    validateInput(input: string, config: InputValidationConfig): EditorResult<string> {
      return AISec.validateInput(input, config);
    },
    filterOutput(output: string, config: OutputFilteringConfig): string {
      return AISec.filterOutput(output, config);
    },
    scanCode(code: string, config: CodeScanningConfig) {
      return AISec.scanCode(code, config);
    },
    updatePersonalization(state: PersonalizationState, preferences: Record<string, unknown>) {
      return AIPersonal.updatePreferences(state, preferences);
    },
    selectModel(requirements: AIConstraints, strategy: RoutingStrategy): AIModelState | null {
      const models = [...aiModelStates.values()];
      return AIModels.selectModel(models, requirements, strategy);
    },
  };

  // ═══ §27 WORKSPACE NAMESPACE ═══

  const workspace = {
    create(name: string, rootFolders?: string[]): EditorResult<WorkspaceState> {
      const result = WSMain.createWorkspace({ name, rootFolders: rootFolders ?? [] });
      if (result.success) workspaces.set(result.value.id, result.value);
      return emitIfOk(result);
    },
    get(wsId: WorkspaceId): WorkspaceState | undefined {
      return workspaces.get(wsId);
    },
    addRootFolder(wsId: WorkspaceId, folder: string): EditorResult<WorkspaceState> {
      const state = workspaces.get(wsId);
      if (!state) return err('NOT_FOUND', 'Workspace not found');
      const result = WSMain.addRootFolder(state, folder);
      if (result.success) workspaces.set(wsId, result.value);
      return emitIfOk(result);
    },
    removeRootFolder(wsId: WorkspaceId, folder: string): EditorResult<WorkspaceState> {
      const state = workspaces.get(wsId);
      if (!state) return err('NOT_FOUND', 'Workspace not found');
      const result = WSMain.removeRootFolder(state, folder);
      if (result.success) workspaces.set(wsId, result.value);
      return emitIfOk(result);
    },
    updateName(wsId: WorkspaceId, name: string): EditorResult<WorkspaceState> {
      const state = workspaces.get(wsId);
      if (!state) return err('NOT_FOUND', 'Workspace not found');
      const result = WSMain.updateWorkspaceName(state, name);
      if (result.success) workspaces.set(wsId, result.value);
      return emitIfOk(result);
    },
    addPanel(wsId: WorkspaceId, panel: PanelState): EditorResult<PanelState[]> {
      const state = workspaces.get(wsId);
      if (!state) return err('NOT_FOUND', 'Workspace not found');
      return WSLayout.addPanel([...state.panels], panel);
    },
    togglePanel(wsId: WorkspaceId, panelId: PanelId): EditorResult<PanelState[]> {
      const state = workspaces.get(wsId);
      if (!state) return err('NOT_FOUND', 'Workspace not found');
      return WSLayout.togglePanel([...state.panels], panelId);
    },
    list(): WorkspaceId[] {
      return [...workspaces.keys()];
    },
  };

  // ═══ §28 GIT NAMESPACE ═══

  const git = {
    openRepository(rootPath: string): EditorResult<GitRepositoryState> {
      const id = asGitRepositoryId(generateId('gitrepo'));
      const state = emptyGitRepositoryState(id, rootPath);
      gitRepos.set(id, state);
      return ok(state, 'GIT_REPO_OPENED', 'Repository opened', [{ domain: 'git', kind: 'opened', timestamp: now() }]);
    },
    get(repoId: GitRepositoryId): GitRepositoryState | undefined {
      return gitRepos.get(repoId);
    },
    parseStatus(output: string): EditorResult<GitFileStatus[]> {
      return GitRepo.parseStatusOutput(output);
    },
    parseLog(output: string): EditorResult<GitCommit[]> {
      return GitCommitMod.parseLogOutput(output);
    },
    listBranches(repoId: GitRepositoryId): GitBranch[] {
      const state = gitRepos.get(repoId);
      if (!state) return [];
      return GitBranchMod.listBranches(state);
    },
    listRemotes(repoId: GitRepositoryId): GitRemote[] {
      const state = gitRepos.get(repoId);
      if (!state) return [];
      return GitRemoteMod.listRemotes(state);
    },
    listStashes(repoId: GitRepositoryId): GitStash[] {
      const state = gitRepos.get(repoId);
      if (!state) return [];
      return GitMain.stashList(state);
    },
    parseBlame(output: string) {
      return GitCommitMod.parseBlameOutput(output);
    },
    parseDiff(rawDiff: string): EditorResult<GitStagingHunk[]> {
      return GitStaging.parseDiffOutput(rawDiff);
    },
    applyPatch(content: string, hunks: GitStagingHunk[]): EditorResult<string> {
      return GitStaging.applyPatch(content, hunks);
    },
    formatCommitMessage(message: string, maxWidth?: number): string {
      return GitCommitMod.formatCommitMessage(message, maxWidth);
    },
    isValidBranchName(name: string): boolean {
      return GitBranchMod.isValidBranchName(name);
    },
    buildCommand(command: string, args: string[], flags?: Record<string, string | boolean>): string {
      return GitHooks.buildGitCommand(command, args, flags);
    },
    formatPRDescription(commits: GitCommit[]): string {
      return GitHooks.formatPRDescription(commits);
    },
    getHookOrder(): GitHookName[] {
      return GitHooks.GIT_HOOK_ORDER;
    },
  };

  // ═══ §29 FILESYSTEM NAMESPACE ═══

  const filesystem = {
    buildTree(files: FileEntry[], rootPath?: string) {
      return FSTree.buildFileTree(files, rootPath);
    },
    flattenTree(root: DirectoryEntry): FileEntry[] {
      return FSTree.flattenTree(root);
    },
    filterTree(root: DirectoryEntry, predicate: (e: FileEntry | DirectoryEntry) => boolean): DirectoryEntry {
      return FSTree.filterTree(root, predicate);
    },
    matchGlob(path: string, pattern: string): boolean {
      return matchGlob(path, pattern);
    },
    detectMimeType(filename: string): string {
      return FSAnalysis.detectMimeType(filename);
    },
    humanFileSize(bytes: number): string {
      return humanFileSize(bytes);
    },
    fileExtension(path: string): string {
      return fileExtension(path);
    },
    fileName(path: string): string {
      return fileName(path);
    },
    parentDirectory(path: string): string {
      return parentDirectory(path);
    },
    joinPath(...segments: string[]): string {
      return joinPath(...segments);
    },
    normalizePath(path: string): string {
      return normalizePath(path);
    },
    searchFiles(content: string, query: SearchQuery): SearchMatch[] {
      return searchFiles(content, query);
    },
    buildSearchRegex(query: SearchQuery): EditorResult<RegExp> {
      return buildSearchRegexFromQuery(query);
    },
    replaceInContent(content: string, matches: SearchMatch[], replacement: string, preserveCase = false): string {
      return FSSearch.replaceInContent(content, matches, replacement, preserveCase);
    },
    preserveCaseReplace(original: string, replacement: string): string {
      return FSSearch.preserveCaseReplace(original, replacement);
    },
    parseAST(content: string, languageId: LanguageId) {
      return FSAst.parseFile(content, languageId);
    },
    queryAST(ast: ASTNode, query: string) {
      return FSAst.queryAST(ast, query);
    },
    extractSymbols(ast: ASTNode, languageId: LanguageId) {
      return FSAst.extractSymbols(ast, languageId);
    },
    applyOTOperation(content: string, op: OTOperation): EditorResult<string> {
      return applyOTOperation(content, op);
    },
    transformOT(op: OTOperation, against: OTOperation): OTOperation {
      return transformOT(op, against);
    },
  };

  // ═══ §30 SEARCH NAMESPACE (3.0.0) ═══

  const search = {
    getState(): SearchState {
      return searchState;
    },
    start(query: SearchQuery, sessionId?: SearchSessionId): SearchSessionId {
      const id = sessionId ?? SearchModule.createSearchSessionId();
      searchState = SearchModule.startSearch(searchState, query, id, now());
      return id;
    },
    complete(sessionId: SearchSessionId): void {
      searchState = SearchModule.completeSearch(searchState, sessionId, now());
    },
    addResult(sessionId: SearchSessionId, uri: string, matches: SearchMatch[]): void {
      const resultId = SearchModule.createSearchResultId();
      const result: SearchResult = { id: resultId, uri, matches };
      searchState = SearchModule.addResult(searchState, sessionId, result);
    },
    clear(sessionId: SearchSessionId): void {
      searchState = SearchModule.clearSearch(searchState, sessionId);
    },
    clearAll(): void {
      searchState = SearchModule.clearAllSearches(searchState);
    },
    getResults(sessionId: SearchSessionId): readonly SearchResult[] | undefined {
      return SearchModule.getSession(searchState, sessionId);
    },
    getTotalMatchCount(sessionId: SearchSessionId): number {
      return SearchModule.getTotalMatchCount(searchState, sessionId);
    },
    searchText(content: string, query: SearchQuery): SearchMatch[] {
      return SearchModule.matchesQuery(content, query);
    },
    buildRegex(query: SearchQuery): RegExp | null {
      return SearchModule.buildSearchRegex(query);
    },
  };

  // ═══ §31 PROJECT NAMESPACE ═══

  const project = {
    create(rootPath: string): EditorResult<ProjectState> {
      const id = asProjectId(generateId('proj'));
      const state = emptyProjectState(id, rootPath);
      projects.set(id, state);
      return ok(state, 'PROJECT_CREATED', 'Project created', [{ domain: 'project', kind: 'created', timestamp: now() }]);
    },
    get(projectId: ProjectId): ProjectState | undefined {
      return projects.get(projectId);
    },
    detectType(files: string[]) {
      return ProjDetect.detectProjectType(files);
    },
    detectPackageManager(files: string[]) {
      return ProjDetect.detectPackageManager(files);
    },
    detectBuildSystem(files: string[]) {
      return ProjDetect.detectBuildSystem(files);
    },
    discoverTasks(files: Array<{ path: string; content: string }>, packageJson?: Record<string, unknown>): ProjectTask[] {
      return ProjDetect.discoverTasks(files, packageJson);
    },
    buildDependencyGraph(deps: DependencyInfo[]): DependencyGraph {
      return ProjDetect.buildDependencyGraph(deps);
    },
    parseEnvFile(content: string): Record<string, string> {
      return ProjEnv.parseDotEnv(content);
    },
    list(): ProjectId[] {
      return [...projects.keys()];
    },
  };

  // ═══ §32 PLUGINS NAMESPACE ═══

  const pluginsNs = {
    register(manifest: PluginManifest): EditorResult<PluginState> {
      const id = asPluginId(generateId('plugin'));
      const state = emptyPluginState(id, manifest);
      const validation = PlugMain.validateManifest(manifest);
      if (!validation.success) return validation as EditorResult<PluginState>;
      pluginStates.set(id, state);
      return ok(state, 'PLUGIN_REGISTERED', `Plugin ${manifest.name} registered`, [{ domain: 'plugin', kind: 'created', timestamp: now() }]);
    },
    get(pluginId: PluginId): PluginState | undefined {
      return pluginStates.get(pluginId);
    },
    activate(pluginId: PluginId): EditorResult<PluginState> {
      const state = pluginStates.get(pluginId);
      if (!state) return err('NOT_FOUND', 'Plugin not found');
      const result = PlugLife.activatePlugin(state);
      if (result.success) pluginStates.set(pluginId, result.value);
      return emitIfOk(result);
    },
    deactivate(pluginId: PluginId): EditorResult<PluginState> {
      const state = pluginStates.get(pluginId);
      if (!state) return err('NOT_FOUND', 'Plugin not found');
      const result = PlugLife.deactivatePlugin(state);
      if (result.success) pluginStates.set(pluginId, result.value);
      return emitIfOk(result);
    },
    checkPermissions(required: readonly PluginPermission[], granted: readonly PluginPermission[]): boolean {
      return PlugSandbox.checkPermissions(required, granted);
    },
    createSandboxConfig(runtime: SandboxConfig['runtime'], permissions: PluginPermission[], overrides?: Partial<SandboxConfig>): SandboxConfig {
      return PlugSandbox.createSandboxConfig(runtime, permissions, overrides);
    },
    list(): PluginId[] {
      return [...pluginStates.keys()];
    },
  };

  // ═══ §33 SETTINGS NAMESPACE ═══

  const settings = {
    getState(): SettingsState {
      return settingsState;
    },
    resolve(key: string): unknown {
      return SettMgr.resolveSetting(settingsState, key);
    },
    update(key: string, value: unknown, scope: SettingsScope): EditorResult<SettingsState> {
      const result = SettMgr.updateSetting(settingsState, key, value, scope);
      if (result.success) settingsState = result.value;
      return result;
    },
    validate(schema: SettingSchema, value: unknown): EditorResult<boolean> {
      return SettMgr.validateSetting(schema, value);
    },
    getEffective(): Record<string, unknown> {
      return SettMgr.getEffectiveSettings(settingsState);
    },
    exportProfile(profileId: SettingsProfileId): EditorResult<Record<string, unknown>> {
      return SettMgr.exportProfile(settingsState, profileId);
    },
    importProfile(name: string, settings_: Record<string, unknown>): EditorResult<SettingsState> {
      const result = SettMgr.importProfile(settingsState, name, settings_);
      if (result.success) settingsState = result.value;
      return result;
    },
    applyMigration(migration: SettingsMigration): SettingsState {
      const scopes = Object.fromEntries(settingsState.scopes) as Record<string, unknown>;
      SettMgr.applyMigration(scopes, migration);
      return settingsState;
    },
  };

  // ═══ §34 DIFF NAMESPACE ═══

  const diff = {
    compute(original: string, modified: string, algorithm?: DiffAlgorithm): EditorResult<DiffResult> {
      return DiffComp.computeDiff(original, modified, algorithm);
    },
    computeChar(a: string, b: string) {
      return DiffComp.computeCharDiff(a, b);
    },
    computeWord(a: string, b: string) {
      return DiffComp.computeWordDiff(a, b);
    },
    detectMoves(hunks: DiffHunk[]): MovedBlock[] {
      return DiffComp.detectMovedBlocks(hunks);
    },
    stats(hunks: DiffHunk[]): DiffStats {
      return DiffComp.computeStats(hunks);
    },
    formatUnified(result: DiffResult, contextLines?: number): string {
      return DiffRender.formatUnifiedDiff(result, contextLines);
    },
    formatSideBySide(hunks: DiffHunk[]) {
      return DiffRender.prepareSideBySide(hunks);
    },
    apply(original: string, diff: DiffResult): EditorResult<string> {
      return DiffMain.applyDiff(original, diff);
    },
    threeWayMerge(base: string, local: string, remote: string): EditorResult<ThreeWayMergeState> {
      return DiffMerge.threeWayMerge(base, local, remote);
    },
    detectConflicts(base: string, local: string, remote: string): MergeConflict[] {
      return DiffMerge.detectConflicts(base.split('\n'), local.split('\n'), remote.split('\n'));
    },
    resolveConflict(state: ThreeWayMergeState, conflictId: string, strategy: MergeResolutionStrategy): EditorResult<ThreeWayMergeState> {
      return DiffMerge.resolveConflict(state, conflictId, strategy);
    },
  };

  // ═══ §35 REVIEW NAMESPACE ═══

  const review = {
    addComment(thread: Parameters<typeof DiffReview.addComment>[1]): EditorResult<FullReviewState> {
      const result = DiffReview.addComment(fullReviewState, thread);
      if (result.success) fullReviewState = result.value;
      return emitIfOk(result);
    },
    replyToComment(threadId: ReviewThreadId, reply: Omit<ReviewComment, 'id' | 'threadId' | 'timestamp'>): EditorResult<FullReviewState> {
      const result = DiffReview.replyToComment(fullReviewState, threadId, reply);
      if (result.success) fullReviewState = result.value;
      return emitIfOk(result);
    },
    resolveThread(threadId: ReviewThreadId): EditorResult<FullReviewState> {
      const result = DiffReview.resolveThread(fullReviewState, threadId);
      if (result.success) fullReviewState = result.value;
      return emitIfOk(result);
    },
    applySuggestion(suggestionId: string): EditorResult<FullReviewState> {
      return DiffReview.applySuggestion(fullReviewState, suggestionId);
    },
    addSuggestion(range: Range, replacement: string): EditorResult<FullReviewState> {
      return DiffReview.addSuggestion(fullReviewState, range, replacement);
    },
    getState(): FullReviewState {
      return fullReviewState;
    },
    summary() {
      return DiffReview.getReviewSummary(fullReviewState);
    },
  };

  // ═══ §36 RECORDING NAMESPACE ═══

  const recording = {
    create(terminalId: TerminalId): EditorResult<RecordingState> {
      const id = asRecordingId(generateId('rec'));
      const state = emptyRecordingState(id, terminalId);
      recordings.set(id, state);
      return ok(state, 'RECORDING_CREATED', 'Recording created', [{ domain: 'recording', kind: 'created', timestamp: now() }]);
    },
    get(recordingId: RecordingId): RecordingState | undefined {
      return recordings.get(recordingId);
    },
    start(recordingId: RecordingId): EditorResult<RecordingState> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      const result = TermRec.startRecording(state);
      if (result.success) recordings.set(recordingId, result.value);
      return emitIfOk(result);
    },
    stop(recordingId: RecordingId): EditorResult<RecordingState> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      const result = TermRec.stopRecording(state);
      if (result.success) recordings.set(recordingId, result.value);
      return emitIfOk(result);
    },
    addFrame(recordingId: RecordingId, data: string, timestamp: number): EditorResult<RecordingState> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      const result = TermRec.addDataFrame(state, data, timestamp);
      if (result.success) recordings.set(recordingId, result.value);
      return emitIfOk(result);
    },
    addBookmark(recordingId: RecordingId, label: string, frameIndex: number): EditorResult<RecordingState> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      const updated = TermRec.addBookmark(state, label, frameIndex);
      recordings.set(recordingId, updated);
      return ok(updated, 'BOOKMARK_ADDED', 'Bookmark added');
    },
    exportAsciinema(recordingId: RecordingId): EditorResult<string> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      return ok(TermRec.exportAsciinema(state), 'OK', 'Exported as asciinema');
    },
    createPlayback(recordingId: RecordingId): EditorResult<PlaybackState> {
      const state = recordings.get(recordingId);
      if (!state) return err('NOT_FOUND', 'Recording not found');
      return ok(TermRec.createPlayback(recordingId, state.frames), 'OK', 'Playback created');
    },
  };

  // ═══ §37 COLLABORATION NAMESPACE ═══

  const collaboration = {
    createShareSession(hostName: string): EditorResult<TerminalCollabState> {
      const sessionId = asShareSessionId(generateId('share'));
      const result = TermCollab.createShareSession(sessionId as string, hostName);
      if (result.success) {
        const shareSessionId = result.value.shareSessionId;
        if (shareSessionId) shareStates.set(shareSessionId as ShareSessionId, result.value);
      }
      return emitIfOk(result);
    },
    addParticipant(sessionId: ShareSessionId, name: string, role: ParticipantRole): EditorResult<TerminalCollabState> {
      const state = shareStates.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Share session not found');
      const result = TermCollab.addParticipant(state, name, role);
      if (result.success) shareStates.set(sessionId, result.value);
      return emitIfOk(result);
    },
    addChatMessage(sessionId: ShareSessionId, participantId: ParticipantId, content: string): EditorResult<TerminalCollabState> {
      const state = shareStates.get(sessionId);
      if (!state) return err('NOT_FOUND', 'Share session not found');
      const updated = TermCollab.addChatMessage(state, participantId, content);
      shareStates.set(sessionId, updated);
      return ok(updated, 'MESSAGE_ADDED', 'Chat message added');
    },
    roleToPermissions(role: ParticipantRole) {
      return TermCollab.roleToPermissions(role);
    },
  };

  // ═══ §38 ADAPTERS NAMESPACE ═══

  const adapters = {
    positionToMonaco(position: Position) {
      return AdMon.positionToMonaco(position);
    },
    positionFromMonaco(monacoPos: { lineNumber: number; column: number }) {
      return AdMon.positionFromMonaco(monacoPos);
    },
    positionToLSP(position: Position) {
      return AdLSP.positionToLSP(position);
    },
    positionFromLSP(lspPos: { line: number; character: number }) {
      return AdLSP.positionFromLSP(lspPos);
    },
    createDAPConfig(type: string, request: 'launch' | 'attach', options?: Partial<DAPClientConfig>) {
      return AdDAP.createDAPConfig(type, request, options);
    },
    createToolAdapterState(config?: Parameters<typeof AdTools.createToolAdapterState>[0]) {
      return AdTools.createToolAdapterState(config);
    },
    formatDocument(state: Parameters<typeof AdTools.formatDocument>[0], content: string, languageId: LanguageId) {
      return AdTools.formatDocument(state, content, languageId);
    },
    lintDocument(state: Parameters<typeof AdTools.lintDocument>[0], content: string, languageId: LanguageId) {
      return AdTools.lintDocument(state, content, languageId);
    },
    typecheckDocument(state: Parameters<typeof AdTools.typecheckDocument>[0], filePath: string) {
      return AdTools.typecheckDocument(state, filePath);
    },
  };

  // ═══ §39 EVENTS NAMESPACE ═══

  const events = {
    on(pattern: string, handler: EventHandler): void {
      bus.on(pattern, handler);
    },
    off(pattern: string, handler: EventHandler): void {
      bus.off(pattern, handler);
    },
    once(pattern: string, handler: EventHandler): void {
      bus.once(pattern, handler);
    },
    emit(event: EditorEvent): void {
      bus.emit(event);
    },
    clear(): void {
      bus.clear();
    },
    createChannel(id: string, name: string, pattern: string) {
      return GlEvents.createChannel(id, name, pattern);
    },
    matchPattern(eventType: string, pattern: string): boolean {
      return GlEvents.matchEventPattern(pattern, eventType);
    },
  };

  // ═══ §40 PERFORMANCE NAMESPACE ═══

  const performance = {
    snapshot(): PerformanceSnapshot {
      const snap = GlPerf.getPerformanceSnapshot(performanceState);
      performanceHistory.push(snap);
      return snap;
    },
    recordFrameTime(durationMs: number): void {
      performanceState = GlPerf.recordFrameTime(performanceState, durationMs);
    },
    getFrameBudgetStatus() {
      return GlPerf.getFrameBudgetStatus(performanceState);
    },
    getHistory(): PerformanceSnapshot[] {
      return performanceHistory;
    },
    detectLongTask(taskName: string, durationMs: number) {
      return GlPerf.detectLongTask(performanceState, taskName, durationMs);
    },
    getMemoryPressure() {
      return GlPerf.getMemoryPressure(performanceState);
    },
  };

  // ═══ §41 STORAGE NAMESPACE ═══

  const storage = {
    get(namespace: StorageNamespace, key: string) {
      return GlStorage.get(storageState, namespace, key);
    },
    set(namespace: StorageNamespace, key: string, value: unknown, options?: { ttlMs?: number; encrypted?: boolean }) {
      const result = GlStorage.set(storageState, namespace, key, value, options);
      if (result.success) storageState = result.value;
      return result;
    },
    delete(namespace: StorageNamespace, key: string) {
      const result = GlStorage.del(storageState, namespace, key);
      if (result.success) storageState = result.value;
      return result;
    },
    list(namespace: StorageNamespace, prefix?: string): string[] {
      return GlStorage.list(storageState, namespace, prefix);
    },
    clear(namespace: StorageNamespace) {
      const result = GlStorage.clear(storageState, namespace);
      if (result.success) storageState = result.value;
      return result;
    },
  };

  // ═══ §42 PERMISSIONS NAMESPACE ═══

  const permissions = {
    grant(request: PermissionRequest, scope: string): EditorResult<typeof permissionsState> {
      const result = GlPerms.grantPermission(permissionsState, request, scope);
      if (result.success) permissionsState = result.value;
      return result;
    },
    check(capability: string, scope?: string): boolean {
      return GlPerms.checkPermission(permissionsState, capability, scope);
    },
    revoke(permissionId: PermissionId) {
      const result = GlPerms.revokePermission(permissionsState, permissionId);
      if (result.success) permissionsState = result.value;
      return result;
    },
    listGrants(): PermissionGrant[] {
      return [...permissionsState.grants];
    },
    getTrustLevel(): TrustLevel {
      return GlPerms.getTrustLevel(permissionsState);
    },
  };

  // ═══ §43 RETURN ═══

  return Object.freeze({
    // Core editing
    document,
    cursor,
    history,
    markers,
    syntax,
    language,
    layout,
    editor,
    find,
    snippets,
    commands,
    keybindings,
    themes,
    // Terminal
    terminal,
    pty,
    session,
    multiplexer,
    buffer,
    ansi,
    output,
    cloud,
    terminalAI,
    recording,
    collaboration,
    // AI
    ai,
    // Workspace & infrastructure
    workspace,
    git,
    filesystem,
    search,
    project,
    plugins: pluginsNs,
    settings,
    diff,
    review,
    adapters,
    // Platform
    events,
    performance,
    storage,
    permissions,

    // Circuits — 16 pre-built composition factories
    circuits: Object.freeze({
      adaptiveWorkspace:    createAdaptiveWorkspace,
      aiAssistedEditor:     createAIAssistedEditor,
      aiCodeReview:         createAICodeReview,
      aiPromptLab:          createAIPromptLab,
      codeEditorFull:       createCodeEditorFull,
      collaborativeEditor:  createCollaborativeEditor,
      diffReview:           createDiffReview,
      editorSession:        createEditorSession,
      gitWorkspace:         createGitWorkspace,
      intelligentTerminal:  createIntelligentTerminal,
      onboardingExperience: createOnboardingExperience,
      projectNavigator:     createProjectNavigator,
      searchWorkspace:      createSearchWorkspace,
      smartTerminal:        createSmartTerminal,
      terminalSession:      createTerminalSession,
      themeApplicator:      createThemeApplicator,
    }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// §44 PER-MODULE IO CONFIG RE-EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export type {
  DocumentIOConfig,
  SyntaxIOConfig,
  LanguageIOConfig,
  LayoutIOConfig,
  EditorIOConfig,
  TerminalIOConfig,
  AIIOConfig,
  WorkspaceIOConfig,
  GitIOConfig,
  FileSystemIOConfig,
  ProjectIOConfig,
  PluginsIOConfig,
  SettingsIOConfig,
  DiffIOConfig,
  AdaptersIOConfig,
  GlobalIOConfig,
  FullEditorIOConfig,
  TerminalSuiteIOConfig,
  AIFullIOConfig,
  CollaborationIOConfig,
  DevWorkflowIOConfig,
  CodeIntelligenceIOConfig,
  IODependencies,
};


// ═══════════════════════════════════════════════════════════════════════════════
// §45 CIRCUITS — Cross-domain composition factories
// ═══════════════════════════════════════════════════════════════════════════════

// Circuit types (selective)
export type {
  CircuitHealth,
  CircuitHealthReport,
  CircuitHandle,
  CircuitStats,
  CircuitTier,
  CircuitMetadata,
} from './circuits/circuit-types.js';

export {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuits/circuit-types.js';

// Circuit registry (selective)
export {
  getCircuitRegistry,
  getCircuitByName,
  getCircuitsByTier,
  getCircuitsByDomain,
  getCircuitCountsByTier,
  getAllDomains,
  getCircuitCount,
  getRegistrySummary,
} from './circuits/circuit-registry.js';

// Circuit factories (escape hatches — named exports for power users)
export { createAdaptiveWorkspace } from './circuits/adaptive-workspace.js';
export { createAIAssistedEditor } from './circuits/ai-assisted-editor.js';
export { createAICodeReview } from './circuits/ai-code-review.js';
export { createAIPromptLab } from './circuits/ai-prompt-lab.js';
export { createCodeEditorFull } from './circuits/code-editor-full.js';
export { createCollaborativeEditor } from './circuits/collaborative-editor.js';
export { createDiffReview } from './circuits/diff-review.js';
export { createEditorSession } from './circuits/editor-session.js';
export { createGitWorkspace } from './circuits/git-workspace.js';
export { createIntelligentTerminal } from './circuits/intelligent-terminal.js';
export { createOnboardingExperience } from './circuits/onboarding-experience.js';
export { createProjectNavigator } from './circuits/project-navigator.js';
export { createSearchWorkspace } from './circuits/search-workspace.js';
export { createSmartTerminal } from './circuits/smart-terminal.js';
export { createTerminalSession } from './circuits/terminal-session.js';
export { createThemeApplicator } from './circuits/theme-applicator.js';
